import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

import { api, apiConfig } from "../api/api";
import FeedbackModal from "../feedback/FeedbackModal";
import Notify from "../ui/Notify";

const Field = styled.div`
  display: flex;
  align-items: stretch;
  margin-top: 0.9rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  overflow: hidden;
`;

const Input = styled.input`
  flex: 1;
  min-width: 0;
  border: 0;
  padding: 10px 12px;
  font-size: 16px; /* 16px: iOS zooms into anything smaller */
  outline: none;
`;

const Suffix = styled.span`
  display: flex;
  align-items: center;
  padding: 0 12px;
  background: #f1f3f5;
  color: #495057;
  font-size: 14px;
  white-space: nowrap;
`;

const Status = styled.div`
  margin-top: 0.5rem;
  font-size: 0.85rem;
  min-height: 1.2em;
  color: ${(props) =>
    props.tone === "ok"
      ? "#0e7a4d"
      : props.tone === "bad"
        ? "#dc3545"
        : "#6c757d"};
`;

const Suggest = styled.button`
  border: 0;
  background: none;
  padding: 0;
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
  font: inherit;
`;

/** What the user types → a host label: lowercase, a-z0-9 and single hyphens. */
const clean = (value, max) =>
  value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+/, "")
    .slice(0, max || 19);

const check = (params) =>
  api.ajaxSession("spectreposSlug", {
    noWorker: true,
    url: apiConfig.appUrl,
    type: "GET",
    params,
  });

/**
 * The shop's web address (`<slug>.spectre-pos.com`), chosen before paying.
 *
 * Only when new shops are born on Chentro: otherwise, or when the user's shop
 * already exists, it never shows and hands straight back to the purchase.
 * The choice is saved on the server (spectrepos_slug), so the shop gets it
 * whether the browser or Paddle's webhook ends up creating it.
 */
const SlugPicker = ({ show, onHide, onChosen }) => {
  const { t } = useTranslation();
  const [info, setInfo] = React.useState(null);
  const [slug, setSlug] = React.useState("");
  const [state, setState] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  // Bumped to ask again about the same text (after a refused save).
  const [recheck, setRecheck] = React.useState(0);
  const done = React.useRef(onChosen);
  done.current = onChosen;

  React.useEffect(() => {
    if (!show) {
      setInfo(null);
      setState(null);
      return;
    }
    let cancelled = false;
    check({})
      .then((res) => {
        if (cancelled) return;
        if (!res || res.backend !== "hbs" || res.locked) {
          onHide && onHide();
          done.current && done.current();
          return;
        }
        const first = res.slug || res.suggestion || "";
        // `info` last: it is what lets the check-as-you-type effect run, and
        // outside React 18's batching each of these renders on its own.
        setState({ ...res, asked: first });
        setSlug(first);
        setInfo(res);
      })
      .catch(() => {
        if (!cancelled) Notify(t("slug_picker.check_failed"), "danger", 8000);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Ask as the user types, a beat after they stop. Every answer is kept with
  // the text it was asked for (`asked`): the server answers with its own
  // normalised slug ("abc-" → "abc"), so comparing against that would leave
  // the button disabled for good.
  React.useEffect(() => {
    if (!info) return;
    if (state && state.asked === slug && !state.stale) return;
    if (!slug) {
      setState({ asked: slug, available: false, reason: "too_short" });
      return;
    }
    setState({ asked: slug, checking: true });
    const timer = setTimeout(() => {
      check({ slug: slug.replace(/-+$/, "") })
        .then((res) =>
          setState((cur) =>
            cur && cur.asked === slug ? { ...res, asked: slug } : cur,
          ),
        )
        .catch(() =>
          setState((cur) =>
            cur && cur.asked === slug ? { asked: slug, error: true } : cur,
          ),
        );
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, info, recheck]);

  const current = state && state.asked === slug ? state : null;
  const ready = !!(current && current.available && !current.checking);
  // The address as it will be: the server's normalised slug + the domain.
  const preview = ready && current.domain ? current.domain : null;
  // A refused address comes with a free one (the server checked it).
  const offerSlug = current?.suggestion || info?.suggestion;
  const offer =
    current &&
    !current.available &&
    !current.checking &&
    offerSlug &&
    offerSlug !== slug
      ? offerSlug
      : null;
  const suffix = info?.domain ? info.domain.replace(/^[^.]+/, "") : "";

  const save = () => {
    if (!ready || busy) return;
    setBusy(true);
    api
      .ajaxSession("spectreposSlug", {
        noWorker: true,
        url: apiConfig.appUrl,
        type: "POST",
        data: { slug: current.slug },
      })
      .then(() => {
        setBusy(false);
        onHide && onHide();
        done.current && done.current();
      })
      .catch(() => {
        setBusy(false);
        // Taken in the meantime, or the server is down: ask again, so the
        // message says which.
        setState((cur) => (cur ? { ...cur, stale: true } : cur));
        setRecheck((n) => n + 1);
        Notify(t("slug_picker.save_failed"), "danger", 8000);
      });
  };

  let status = null;
  if (current?.checking)
    status = { tone: "muted", text: t("slug_picker.checking") };
  else if (current?.error)
    status = { tone: "bad", text: t("slug_picker.check_failed") };
  else if (current && current.available)
    status = {
      tone: "ok",
      text: t("slug_picker.available_at", { domain: preview }),
    };
  else if (current)
    status = {
      tone: "bad",
      text: t(`slug_picker.${current.reason || "taken"}`),
    };

  return (
    <FeedbackModal
      show={show && !!info}
      onHide={onHide}
      severity="info"
      title={t("slug_picker.title")}
      message={
        <div>
          <div>{t("slug_picker.intro")}</div>
          <Field>
            <Input
              value={slug}
              autoFocus
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              aria-label={t("slug_picker.title")}
              onChange={(e) => setSlug(clean(e.target.value, info?.maxLength))}
              onKeyDown={(e) => e.key === "Enter" && save()}
            />
            <Suffix>{suffix}</Suffix>
          </Field>
          <Status tone={status?.tone}>
            {status?.text}
            {offer && (
              <>
                {" "}
                <Suggest type="button" onClick={() => setSlug(offer)}>
                  {t("slug_picker.use_suggestion", { slug: offer })}
                </Suggest>
              </>
            )}
          </Status>
          <Status tone="muted">{t("slug_picker.hint")}</Status>
        </div>
      }
      secondaryAction={{
        label: t("slug_picker.cancel"),
        onClick: () => onHide && onHide(),
      }}
      primaryAction={{
        label: busy ? t("slug_picker.saving") : t("slug_picker.continue"),
        onClick: save,
        disabled: !ready || busy,
      }}
    />
  );
};

export default SlugPicker;
